import { Graphics, Text, type Application } from 'pixi.js';
import type { World, ComponentStore } from '@ecs/world';
import type {
	CameraResource,
	DeathMarkerComponent,
	StageBounds,
	TransformComponent
} from '@game/scenes/characterPrototype/types';

export type SpawnDeathMarkerOptions = {
	boundsKey: string;
	textMargin: number;
	textYOffset: number;
	heightThreshold: number;
};

export function spawnDeathMarker(
	world: World,
	deathMarkers: ComponentStore<DeathMarkerComponent>,
	transforms: ComponentStore<TransformComponent>,
	deathMarkerGraphics: ComponentStore<Graphics>,
	deathMarkerTexts: ComponentStore<Text>,
	stage: Application['stage'] | null,
	x: number,
	y: number,
	height: number,
	attemptNumber: number,
	options: SpawnDeathMarkerOptions
): void {
	if (!stage) return;

	const bounds = world.getResource<StageBounds>(options.boundsKey);
	if (!bounds) return;

	const entity = world.createEntity();

	// Check if there are other markers at the same height (within threshold)
	// If found, remove the old one and replace with new
	const toRemove: number[] = [];

	transforms.forEach((_existingTransform, existingEntity) => {
		if (existingEntity === entity) return; // Skip self
		const existingMarker = deathMarkers.get(existingEntity);
		if (!existingMarker) return;

		// Check if markers are at similar height
		const heightDiff = Math.abs(existingMarker.height - height);
		if (heightDiff <= options.heightThreshold) {
			// Mark for removal - new marker will replace this one
			toRemove.push(existingEntity);
		}
	});

	// Remove existing markers with same height
	for (const existingEntity of toRemove) {
		const existingGraphic = deathMarkerGraphics.get(existingEntity);
		if (existingGraphic) {
			existingGraphic.parent?.removeChild(existingGraphic);
			existingGraphic.destroy();
		}
		const existingText = deathMarkerTexts.get(existingEntity);
		if (existingText) {
			existingText.parent?.removeChild(existingText);
			existingText.destroy();
		}
		deathMarkers.delete(existingEntity);
		deathMarkerGraphics.delete(existingEntity);
		deathMarkerTexts.delete(existingEntity);
		world.destroyEntity(existingEntity);
	}

	deathMarkers.set(entity, { height, attemptNumber });
	transforms.set(entity, { x, y });

	const camera = world.getResource<CameraResource>('camera');
	const offsetY = camera ? camera.y : 0;

	// Determine if marker is on left or right side to orient flag correctly
	const isLeftSide = x < bounds.width / 2;
	const flagWidth = 10;
	const flagHeight = 8;

	// Create text first to get its width for line
	const text = new Text({
		text: `#${attemptNumber} ${Math.round(height)}m`,
		style: {
			fontFamily: 'Arial',
			fontSize: 14,
			fill: 0x000000,
			align: isLeftSide ? 'left' : 'right'
		}
	});
	// Text position with margin from screen edge
	const textX = isLeftSide ? options.textMargin : bounds.width - options.textMargin;
	text.anchor.set(isLeftSide ? 0 : 1, 1);
	text.position.set(textX, y - offsetY + options.textYOffset);
	stage.addChild(text);
	deathMarkerTexts.set(entity, text);

	// Create marker graphic (red line from screen edge to end of text + rotated flag at the end)
	const graphic = new Graphics();
	// Draw red line under text
	const lineY = options.textYOffset + 2; // slightly below text baseline
	const lineStartX = isLeftSide ? 0 - x : bounds.width - options.textMargin - text.width - x;
	const lineEndX = isLeftSide ? options.textMargin + text.width - x : bounds.width - x;
	graphic.moveTo(lineStartX, lineY);
	graphic.lineTo(lineEndX, lineY);
	graphic.stroke({ color: 0xff3b30, alpha: 0.9, width: 2 });
	// Draw flag at the end of the line
	const flagX = isLeftSide ? lineEndX : lineStartX - flagHeight;
	const flagY = lineY - flagWidth / 2;
	graphic.rect(flagX, flagY, flagHeight, flagWidth);
	graphic.fill({ color: 0xff3b30, alpha: 0.9 });
	graphic.stroke({ color: 0xcc2e24, alpha: 0.8, width: 1 });
	graphic.position.set(x, y - offsetY);
	stage.addChild(graphic);
	deathMarkerGraphics.set(entity, graphic);
}


